import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

export const CONTINUOUS_SLEEP_TYPE = 'P360_Sleep';

const parseTimestamp = (value) => (typeof value === 'string' ? Date.parse(value) : NaN);
const isCalendarDate = (value) =>
    typeof value === 'string' &&
    /^\d{4}-\d{2}-\d{2}$/.test(value) &&
    dayjs.utc(value).isValid() &&
    dayjs.utc(value).format('YYYY-MM-DD') === value;

export const normalizeContinuousSleep = (
    table,
    { startTimestamp, endTimestamp, zone }
) => {
    const start = Date.parse(startTimestamp);
    const end = Date.parse(endTimestamp);
    if (!Number.isFinite(start) || !Number.isFinite(end) || start >= end) return [];
    const firstDate = dayjs(start).tz(zone).format('YYYY-MM-DD');
    const endDate = dayjs(end).tz(zone).format('YYYY-MM-DD');
    const nights = new Map();
    const columns = Array.isArray(table?.columns) ? table.columns : [];
    const dateIndex = columns.indexOf('date');
    const resultIndex = columns.indexOf('result');
    const timeIndex = columns.indexOf('ts');
    for (const row of Array.isArray(table?.data) ? table.data : []) {
        if (
            dateIndex < 0 ||
            resultIndex < 0 ||
            timeIndex < 0 ||
            !Array.isArray(row) ||
            row.length !== columns.length
        )
            continue;
        const date = row[dateIndex];
        const result = row[resultIndex];
        if (
            !isCalendarDate(date) ||
            date < firstDate ||
            date >= endDate ||
            !result ||
            typeof result !== 'object' ||
            Array.isArray(result)
        )
            continue;
        const sleepStart = parseTimestamp(result.sleepStartTime);
        const sleepEnd = parseTimestamp(result.sleepEndTime);
        if (
            !Number.isFinite(sleepStart) ||
            !Number.isFinite(sleepEnd) ||
            sleepEnd <= sleepStart
        )
            continue;
        const revision = [result.lastModified, row[timeIndex]]
            .map(parseTimestamp)
            .find(Number.isFinite);
        if (!Number.isFinite(revision)) continue;
        if (nights.has(date) && revision < nights.get(date).revision) continue;
        nights.set(date, {
            date,
            sleepStart,
            sleepEnd,
            durationMinutes: (sleepEnd - sleepStart) / 60000,
            goalMinutes:
                typeof result.sleepGoalMinutes === 'number' &&
                Number.isFinite(result.sleepGoalMinutes) &&
                result.sleepGoalMinutes > 0
                    ? result.sleepGoalMinutes
                    : null,
            revision
        });
    }
    const days = [];
    for (
        let date = firstDate;
        date < endDate;
        date = dayjs.utc(date).add(1, 'day').format('YYYY-MM-DD')
    ) {
        const night = nights.get(date);
        days.push({
            date,
            sleepStart: night?.sleepStart ?? null,
            sleepEnd: night?.sleepEnd ?? null,
            durationMinutes: night?.durationMinutes ?? null,
            goalMinutes: night?.goalMinutes ?? null
        });
    }
    return days;
};

export const summarizeSleep = (days) => {
    const recorded = days.filter((day) => day.durationMinutes !== null);
    return {
        recorded: recorded.length,
        averageMinutes: recorded.length
            ? recorded.reduce((sum, day) => sum + day.durationMinutes, 0) /
              recorded.length
            : null
    };
};

export const buildSleepChartData = (days, period) => {
    if (period === '7d' || period === '30d') {
        return days.map((day) => ({
            ...day,
            endDate: day.date,
            recorded: day.durationMinutes === null ? 0 : 1
        }));
    }
    const groups = new Map();
    days.forEach((day, index) => {
        const key = period === 'all' ? day.date.slice(0, 7) : Math.floor(index / 7);
        if (!groups.has(key)) groups.set(key, []);
        groups.get(key).push(day);
    });
    return [...groups.values()].map((group) => {
        const summary = summarizeSleep(group);
        const goals = group.filter(
            (day) => day.durationMinutes !== null && day.goalMinutes !== null
        );
        return {
            date: group[0].date,
            endDate: group.at(-1).date,
            durationMinutes: summary.averageMinutes,
            recorded: summary.recorded,
            goalMinutes: goals.length
                ? goals.reduce((sum, day) => sum + day.goalMinutes, 0) / goals.length
                : null
        };
    });
};

export const formatSleepDuration = (minutes) => {
    if (!Number.isFinite(minutes)) return '—';
    const total = Math.round(minutes);
    return `${Math.floor(total / 60)}h ${String(total % 60).padStart(2, '0')}m`;
};
